/**
 * Functions for performing fetch calls with an authorization
 * @module
 */
import Flags from "common/constants/flags";
import { gzipCompressString } from "common/utils/compressionBrowser";
import { fetchWithTimeout, fetchWithTimeoutAndRetry } from "common/utils/fetchHelpers";
import { getToken } from "lib/ctx";

// These functions should be kept separate from the more generic
// `fetchWithTimeout` which may be invoked server side.

export const DEPLOYMENT_ID_MISMATCH = "x-deployment-id-mismatch";

/**
 * Adds "middleware" to window.fetch by intercepting the response
 * This is loaded at the beginning of _app.tsx
 */
export async function loadClientFetchMiddleware() {
  if (typeof window === "undefined") {
    return;
  }

  const { fetch: originalFetch } = window;

  window.fetch = async (...args) => {
    const response = await originalFetch(...args);

    // If a server response includes deployment mismatch, dispatch an event so
    // the app shell can reload onto the fresh bundle (consumed in main.tsx).
    const deploymentIdMismatch = response.headers.get(DEPLOYMENT_ID_MISMATCH);
    if (deploymentIdMismatch && Flags.ENABLE_CLIENT_RELOAD_ON_DEPLOYMENT_CHANGE) {
      const event = new CustomEvent(DEPLOYMENT_ID_MISMATCH, { detail: deploymentIdMismatch });
      window.dispatchEvent?.(event);
    }

    return response;
  };
}

// Generate client headers with or without auth
export async function getFetchHeaders(withAuth: boolean, customHeaders: Record<string, string> = {}) {
  const headers: Record<string, string> = {
    Accept: "application/json",
    "Content-Type": "application/json",
    "X-Deployment-ID": Flags.GIT_COMMIT_HASH,
    ...customHeaders,
  };

  if (withAuth) {
    const token = await getToken();

    if (token) {
      headers["authorization"] = token;
    }
  }

  return headers;
}

/** Drop-in `fetch` that merges auth headers from {@link getFetchHeaders}. */
export const authFetch: typeof fetch = async (input, init) => {
  const baseHeaders = await getFetchHeaders(true);
  const merged = new Headers(baseHeaders);
  if (init?.headers) {
    new Headers(init.headers).forEach((value, key) => {
      merged.set(key, value);
    });
  }
  return fetch(input, { ...init, headers: merged });
};

export async function getWithTimeout(path: string): Promise<Response> {
  return fetchWithTimeout(path, {
    method: "GET",
    headers: await getFetchHeaders(true),
  });
}

export async function getWithTimeoutAndRetry(path: string, retries?: number, timeoutMs?: number): Promise<Response> {
  return fetchWithTimeoutAndRetry(
    path,
    {
      method: "GET",
      headers: await getFetchHeaders(true),
    },
    retries,
    timeoutMs,
  );
}

export async function putWithTimeout<Body = any>(path: string, body?: Body, timeout?: number): Promise<Response> {
  return putWithTimeoutNoStringify({ path, body: JSON.stringify(body), timeout });
}

export async function putWithTimeoutNoStringify({
  path,
  body,
  timeout,
  compressBody = false,
}: {
  path: string;
  body: string;
  timeout?: number;
  compressBody?: boolean;
}): Promise<Response> {
  const headers = await getFetchHeaders(true);

  let finalBody: string | Uint8Array<ArrayBuffer> = body;

  if (compressBody) {
    finalBody = await gzipCompressString(body);

    headers["Content-Encoding"] = "gzip";
  }

  return fetchWithTimeout(
    path,
    {
      method: "PUT",
      body: finalBody,
      headers,
    },
    timeout,
  );
}

export async function patchWithTimeoutNoStringify({
  path,
  body,
  timeout,
  compressBody = false,
}: {
  path: string;
  body: string;
  timeout?: number;
  compressBody?: boolean;
}): Promise<Response> {
  const headers = await getFetchHeaders(true);

  let finalBody: string | Uint8Array<ArrayBuffer> = body;

  if (compressBody) {
    finalBody = await gzipCompressString(body);

    headers["Content-Encoding"] = "gzip";
  }

  return fetchWithTimeout(
    path,
    {
      method: "PATCH",
      body: finalBody,
      headers,
    },
    timeout,
  );
}

export async function putFormDataWithTimeout(path: string, body: File | FormData, timeout?: number): Promise<Response> {
  const headers = await buildHeadersAddAuthIfPossible();

  return fetchWithTimeout(
    path,
    {
      method: "PUT",
      body,
      headers,
    },
    timeout,
  );
}

async function buildHeadersAddAuthIfPossible(): Promise<HeadersInit> {
  const authorization = await getToken();

  const headers = {
    "X-Deployment-ID": Flags.GIT_COMMIT_HASH,
  };

  if (authorization) {
    headers["authorization"] = authorization;
  }

  return headers;
}

export async function patchWithTimeout<Body = any>(path: string, body: Body, timeout?: number): Promise<Response> {
  return fetchWithTimeout(
    path,
    {
      method: "PATCH",
      body: JSON.stringify(body),
      headers: await getFetchHeaders(true),
    },
    timeout,
  );
}

export async function postFormDataWithTimeout(
  path: string,
  body: File | FormData,
  timeout?: number,
): Promise<Response> {
  const headers = await buildHeadersAddAuthIfPossible();

  return fetchWithTimeout(
    path,
    {
      method: "POST",
      body,
      headers,
    },
    timeout,
  );
}

export async function postWithTimeout<Body = any>(path: string, body?: Body, timeout?: number): Promise<Response> {
  return postWithTimeoutNoStringify({ path, body: JSON.stringify(body), timeout });
}

export async function postWithTimeoutNoStringify({
  path,
  body,
  timeout,
  compressBody = false,
}: {
  path: string;
  body: string;
  timeout?: number;
  compressBody?: boolean;
}): Promise<Response> {
  const headers = await getFetchHeaders(true);
  let finalBody: string | Uint8Array<ArrayBuffer> = body;

  if (compressBody) {
    finalBody = await gzipCompressString(body);

    headers["Content-Encoding"] = "gzip";
  }

  return fetchWithTimeout(
    path,
    {
      method: "POST",
      body: finalBody,
      headers,
    },
    timeout,
  );
}

export async function postWithTimeoutNoAuth<Body = any>(
  path: string,
  body?: Body,
  timeout?: number,
): Promise<Response> {
  return fetchWithTimeout(
    path,
    {
      method: "POST",
      body: JSON.stringify(body),
      headers: await getFetchHeaders(false),
    },
    timeout,
  );
}

export async function deleteWithTimeout<Body = any>(path: string, body?: Body, timeout?: number): Promise<Response> {
  return fetchWithTimeout(
    path,
    {
      method: "DELETE",
      body: body ? JSON.stringify(body) : "",
      headers: await getFetchHeaders(true),
    },
    timeout,
  );
}

export interface FileUploadWithTimeoutParams {
  path: string;
  fileName: string;
  file: File;
  timeout?: number;
  body?: Record<string, any>;
}

export async function fileUploadWithTimeout({
  path,
  fileName,
  file,
  timeout,
  body = {},
}: FileUploadWithTimeoutParams): Promise<Response> {
  const formData = new FormData();
  formData.append(fileName, file);

  for (const param in body) {
    formData.append(param, JSON.stringify(body[param]));
  }

  const headers = await buildHeadersAddAuthIfPossible();

  headers["Accept"] = "application/json";

  return fetchWithTimeout(
    path,
    {
      method: "POST",
      body: formData,
      headers,
    },
    timeout,
  );
}
